import React, { useState, useEffect } from 'react';
import { FiHome, FiUsers, FiCheckCircle, FiClock, FiGrid, FiActivity, FiSmartphone, FiDownload } from 'react-icons/fi';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Donut
} from 'recharts';
import './AdminPages.css';
import AppInstallsModal from '../Components/AppInstallsModal';
import VisitorAnalyticsSection from '../Components/VisitorAnalyticsSection';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const DashboardOverview = () => {
  const [loading, setLoading] = useState(true);
  const [syncingInstalls, setSyncingInstalls] = useState(false);
  const [installsModalOpen, setInstallsModalOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    } finally {
      setLoading(false);
    }
  };

  const syncAppInstalls = async () => {
    setSyncingInstalls(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API_URL}/admin/analytics/app-installs/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData((prev) => prev ? {
        ...prev,
        summary: { ...prev.summary, appInstalls: res.data }
      } : prev);
      if (res.data?.sync_warning) {
        toast.warning(res.data.sync_warning);
      } else {
        toast.success(`Install count updated: ${res.data?.install_label ?? 'OK'}`);
      }
    } catch (e) {
      console.error('Install sync failed', e);
      toast.error(e.response?.data?.error || 'Failed to sync install count from Play Store');
    } finally {
      setSyncingInstalls(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Analytics Dashboard</h1>
          <p>Loading your insights...</p>
        </div>
        <div className="stats-grid">
          {[1, 2, 3, 4].map(i => <div key={i} className="admin-card skeleton" style={{height: '100px'}}></div>)}
        </div>
        <div className="analytics-charts-grid">
          {[1, 2, 3, 4].map(i => <div key={i} className="admin-card skeleton" style={{height: '350px'}}></div>)}
        </div>
      </div>
    );
  }

  if (!data) return <div>Failed to load data.</div>;

  const { summary, charts } = data;

  const stats = [
    { title: 'Total Properties', value: summary.totalProperties, icon: <FiHome />, color: '#3b82f6' },
    { title: 'Total Projects', value: summary.totalProjects, icon: <FiGrid />, color: '#8b5cf6' },
    { title: 'Active Listings', value: summary.activeListings, icon: <FiCheckCircle />, color: '#10b981' },
    { title: 'Awaiting Approval', value: summary.pendingListings, icon: <FiClock />, color: '#f59e0b' },
    { title: 'Total Partners', value: summary.totalPartners, icon: <FiUsers />, color: '#ec4899' },
    { title: 'Total Users', value: summary.totalUsers, icon: <FiActivity />, color: '#14b8a6' },
    { title: 'Users Online', value: summary.onlineUsers ?? 0, icon: <FiUsers />, color: '#22c55e' },
    {
      title: 'App Installs',
      value:
        summary.appInstalls?.exact_install_count != null
          ? Number(summary.appInstalls.exact_install_count).toLocaleString('en-IN')
          : (summary.appInstalls?.install_label ?? '—'),
      icon: <FiDownload />,
      color: '#c8a04a',
      sub: summary.appInstalls?.install_label && summary.appInstalls.install_label !== '—'
        ? `Play Store: ${summary.appInstalls.install_label}${
            summary.appInstalls?.last_synced_at
              ? ` · Synced ${new Date(summary.appInstalls.last_synced_at).toLocaleDateString()}`
              : ''
          }`
        : (summary.appInstalls?.last_synced_at
            ? `Synced ${new Date(summary.appInstalls.last_synced_at).toLocaleDateString()}`
            : 'Tracked installs from registered devices'),
      clickable: true,
    },
  ];

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1>Analytics Dashboard</h1>
          <p>Real-time insights and platform metrics.</p>
        </div>
        <a
          href="https://play.google.com/store/apps/details?id=com.phoneix.pif&hl=en_IN"
          target="_blank"
          rel="noopener noreferrer"
          className="admin-card"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', textDecoration: 'none', color: '#a67c2e', fontWeight: 600 }}
        >
          <FiSmartphone /> Play Store
        </a>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {stats.map((stat, i) => (
          <div key={i} className="admin-card stat-card">
            <div className="stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-info">
              <h3
                style={stat.clickable ? { cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' } : undefined}
                onClick={stat.clickable ? () => setInstallsModalOpen(true) : undefined}
                onKeyDown={stat.clickable ? (e) => e.key === 'Enter' && setInstallsModalOpen(true) : undefined}
                role={stat.clickable ? 'button' : undefined}
                tabIndex={stat.clickable ? 0 : undefined}
                title={stat.clickable ? 'View installed devices' : undefined}
              >
                {stat.value}
              </h3>
              <p>{stat.title}</p>
              {stat.sub && <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{stat.sub}</small>}
              {stat.title === 'App Installs' && (
                <button
                  type="button"
                  onClick={syncAppInstalls}
                  disabled={syncingInstalls}
                  style={{ marginTop: '0.35rem', fontSize: '0.75rem', border: 'none', background: 'none', color: '#c8a04a', cursor: syncingInstalls ? 'wait' : 'pointer', fontWeight: 600, opacity: syncingInstalls ? 0.6 : 1 }}
                >
                  {syncingInstalls ? 'Syncing...' : 'Sync from Play Store'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="analytics-charts-grid">
        
        {/* Property vs Project Distribution */}
        <div className="admin-card chart-card">
          <h2>Property vs Project</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={charts.propertyProjectDist}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {charts.propertyProjectDist.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Listings Status Breakdown */}
        <div className="admin-card chart-card">
          <h2>Listings Status</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.statusBreakdown}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip cursor={{fill: '#f1f5f9'}} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {charts.statusBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={
                      entry.name === 'Active' ? '#10b981' :
                      entry.name === 'Pending' ? '#f59e0b' :
                      entry.name === 'Rejected' ? '#ef4444' : '#94a3b8'
                    } />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="admin-card chart-card chart-wide">
          <h2>Monthly Creation Trend (Last 6 Months)</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts.monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="Properties" stroke="#3b82f6" strokeWidth={3} dot={{r: 4, strokeWidth: 2}} activeDot={{r: 6}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="admin-card chart-card">
          <h2>Property Features</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.urgentHighlightedDist} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip cursor={{fill: '#f1f5f9'}} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="admin-card chart-card">
          <h2>Top Cities</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.locationDistribution}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{fontSize: 12}} interval={0} angle={-45} textAnchor="end" height={60} />
                <YAxis />
                <Tooltip cursor={{fill: '#f1f5f9'}} />
                <Bar dataKey="value" fill="#ec4899" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <VisitorAnalyticsSection />

      <AppInstallsModal
        open={installsModalOpen}
        onClose={() => {
          setInstallsModalOpen(false);
          fetchAnalytics();
        }}
        playStoreLabel={summary.appInstalls?.install_label}
      />
    </div>
  );
};

export default DashboardOverview;
